import { MutableRefObject } from 'react';

import { ScriptNode } from '@core/assistant/providers/scriptPlayer/linearScriptPlayer';
import AssistantQueryService from '@services/AssistantQueryService';

import { TutorialHighlightKey, TutorialId, TutorialStep } from '../interfaces';

export interface BuildScriptNodesDeps {
  tutorialId: TutorialId;
  setHighlight: (key: TutorialHighlightKey | null) => void;
  setKeyHint: (label: string | null) => void;
  setStepText: (text: string | null) => void;
  currentAudioRef: MutableRefObject<HTMLAudioElement | null>;
}

/**
 * Converts a tutorial's declarative steps into `LinearScriptPlayer` nodes,
 * wiring narration playback (via the assistant's TTS service) and
 * highlight-target/key-hint/step-text state to the player's node lifecycle.
 * Narration failures are swallowed — audio is an enhancement and must never
 * stall the tutorial.
 *
 * @param steps - Ordered tutorial steps to convert.
 * @param deps.tutorialId - Id of the owning tutorial, included in error logs.
 * @param deps.setHighlight - Sets which PulsingBorder target is currently active.
 * @param deps.setKeyHint - Sets which letter the floating `KeyCap` hint currently shows.
 * @param deps.setStepText - Sets the narration text shown in the step-text box.
 * @param deps.currentAudioRef - Ref tracking the in-flight narration `Audio` element, so callers can pause/clean it up independently of the player.
 * @returns One `ScriptNode` per step, ready to hand to `LinearScriptPlayer`.
 */
export const buildScriptNodes = (
  steps: TutorialStep[],
  {
    tutorialId,
    setHighlight,
    setKeyHint,
    setStepText,
    currentAudioRef,
  }: BuildScriptNodesDeps,
): ScriptNode[] => {
  return steps.map((step) => {
    const text = step.narration;

    return {
      id: step.id,
      text,
      allowedKeys: step.awaitKeys ?? 'NONE',
      clickSelectors: step.awaitClickSelector
        ? [step.awaitClickSelector]
        : undefined,
      play: async () => {
        console.info('Guided tutorial: step action starting', {
          tutorialId,
          stepId: step.id,
          highlightTarget: step.highlightTarget ?? null,
        });

        try {
          const buffer = await AssistantQueryService.generateAssistantAudio({
            text,
            disableOptimizations: true,
          });
          const url = URL.createObjectURL(
            new Blob([buffer], { type: 'audio/mpeg' }),
          );
          const audioEl = new Audio(url);

          audioEl.addEventListener('ended', () => URL.revokeObjectURL(url), {
            once: true,
          });
          currentAudioRef.current = audioEl;
          await audioEl.play();
        } catch (error) {
          console.warn(
            `Failed to play tutorial narration: ${(error as Error).message}`,
            { tutorialId, stepId: step.id },
          );
        }

        setStepText(text);

        if (step.highlightTarget) {
          setHighlight(step.highlightTarget);
        }

        if (step.keyHintLabel) {
          setKeyHint(step.keyHintLabel);
        }

        console.info('Guided tutorial: step action finished', {
          tutorialId,
          stepId: step.id,
          highlightTarget: step.highlightTarget ?? null,
        });
      },
    };
  });
};
